import { useEffect, useRef } from 'react'
import { Mail } from 'lucide-react'
import { checkRecipients } from '@/services/orderService'
import type { EmailRow } from '@/lib/recipientRow'

// ช่องพิมพ์อีเมลผู้รับสิทธิ์ (ใช้ทั้งฟอร์มซื้อให้พนักงานในหน้าสมัครคอร์ส ตะกร้า และฟอร์มผู้เข้าอบรม Onsite)
// ไม่มี dropdown ค้นหา/แนะนำอีเมลแล้ว — ผู้ใช้พิมพ์อีเมลเองให้ครบ (กันการไล่ดูอีเมลสมาชิกอื่น และรองรับสมาชิกจำนวนมาก)
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
const inputClass =
  'w-full rounded-sm border border-border bg-surface-2 px-3.5 py-2.5 text-sm text-text placeholder:text-text-muted/70 outline-none transition focus:border-indigo-400'

// เมื่อพิมพ์อีเมลครบรูปแบบ ระบบตรวจ "อีเมลนั้นอีเมลเดียว" ว่ามีบัญชี CoWork หรือไม่ (lookup=true; เฉพาะบัญชีบริษัทที่ฝั่ง API อนุญาต)
// requireAccount=true (ซื้อคอร์สออนไลน์ให้พนักงาน): ต้องมีบัญชี — ไม่พบ = ผิด
// requireAccount=false (ผู้เข้าอบรม Onsite): ไม่จำเป็นต้องมีบัญชี — ไม่พบก็ใช้ต่อได้ แต่ถ้ามีบัญชีคอร์สจะเข้าบัญชีอัตโนมัติตอนชำระเงิน
// (backend ตรวจอีเมลซ้ำ/ลงทะเบียนรุ่นนี้แล้วให้อีกชั้นตอนชำระเงิน)
export default function RecipientEmailInput({
  value, onChange, courseId, batchId, takenEmails, requireAccount, lookup, placeholder, withIcon,
}: {
  value: EmailRow
  onChange: (next: EmailRow) => void
  courseId: number
  batchId?: number | null      // Onsite: เช็คว่าลงทะเบียน "รุ่นนี้" แล้วหรือยัง
  takenEmails: string[]        // อีเมลที่ใส่ไว้ในแถว/คนอื่นแล้ว — ซ้ำไม่ได้
  requireAccount: boolean
  lookup: boolean              // ตรวจว่าอีเมลที่พิมพ์มีบัญชีหรือไม่ (false = ช่องพิมพ์ธรรมดา เช่น บัญชีบุคคลทั่วไปที่ API ไม่อนุญาตให้ตรวจ)
  placeholder: string
  withIcon?: boolean
}) {
  const requestId = useRef(0)
  const q = value.email.trim().toLowerCase()
  const duplicate = q !== '' && takenEmails.includes(q)
  const checkable = lookup && EMAIL_RE.test(q) && !duplicate

  // ตรวจอีเมลที่พิมพ์ครบแล้ว (หน่วง 400ms กันยิงทุกตัวอักษร) — ผลที่ตอบกลับช้ากว่าอีเมลล่าสุดจะถูกทิ้ง
  useEffect(() => {
    if (!checkable) return
    const id = ++requestId.current
    const timer = setTimeout(() => {
      checkRecipients(courseId, [q], batchId)
        .then((results) => {
          if (id !== requestId.current) return
          const r = results[0]
          onChange({
            email: value.email,
            status: r?.status === 'OK' ? 'ok' : r?.status === 'ALREADY_ENROLLED' ? 'enrolled' : 'notfound',
            name: r?.display_name,
          })
        })
        .catch(() => { if (id === requestId.current) onChange({ email: value.email, status: 'error' }) })
    }, 400)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, courseId, batchId, checkable])

  const handleType = (text: string) => {
    requestId.current++
    const t = text.trim().toLowerCase()
    onChange({ email: text, status: lookup && EMAIL_RE.test(t) ? 'searching' : 'idle' })
  }

  const notFoundText = requireAccount
    ? { text: 'ไม่พบอีเมลนี้ในระบบ (พนักงานต้องสมัครสมาชิก CoWork ก่อน)', cls: 'text-rose-500' }
    : { text: 'ไม่พบบัญชี CoWork ของอีเมลนี้ — ยังบันทึกรายชื่อได้ คอร์สจะเข้าบัญชีเมื่อเขาสมัครสมาชิกด้วยอีเมลนี้', cls: 'text-text-muted' }
  const statusText = duplicate ? { text: 'อีเมลนี้ถูกเพิ่มในรายการแล้ว', cls: 'text-rose-500' }
    : !lookup ? null
    : value.status === 'ok' ? { text: `พบบัญชี CoWork: ${value.name ?? ''}`, cls: 'text-emerald-600' }
    : value.status === 'notfound' ? notFoundText
    : value.status === 'enrolled' ? { text: batchId ? 'อีเมลนี้ลงทะเบียนรุ่นนี้แล้ว' : 'อีเมลนี้ลงทะเบียนคอร์สนี้แล้ว', cls: 'text-rose-500' }
    : value.status === 'error'
      ? { text: requireAccount ? 'ตรวจสอบอีเมลไม่สำเร็จ กรุณาลองใหม่' : 'ตรวจสอบบัญชีไม่สำเร็จ — ยังบันทึกรายชื่อได้', cls: requireAccount ? 'text-rose-500' : 'text-text-muted' }
    : value.status === 'searching' ? { text: 'กำลังตรวจสอบ...', cls: 'text-text-muted' }
    : null
  const borderCls =
    duplicate ? 'border-rose-300'
    : !lookup ? ''
    : value.status === 'ok' ? 'border-emerald-400'
    : value.status === 'enrolled' || (requireAccount && (value.status === 'notfound' || value.status === 'error')) ? 'border-rose-300'
    : ''

  return (
    <div>
      <div className="relative">
        {withIcon && <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />}
        <input
          type="email"
          value={value.email}
          onChange={(e) => handleType(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          className={`${inputClass} ${withIcon ? 'pl-9' : ''} ${borderCls}`}
        />
      </div>
      {statusText && <p className={`mt-1 text-xs ${statusText.cls}`}>{statusText.text}</p>}
    </div>
  )
}
